import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, Phone, MessageSquare, Calendar, ArrowUpRight, 
  MapPin, Clock, Send, CheckCircle, AlertCircle,
  User, Building, Zap, Users, Star, Quote
} from 'lucide-react';
import IconWrapper from '../components/IconWrapper';

// Contact Method Card Component
const ContactCard = ({ icon: Icon, title, value, description, link, delay = 0, iconVariant = 'neon' }) => (
  <motion.a
    href={link}
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className="group relative overflow-hidden w-full block"
  >
    <div className="relative bg-gradient-to-br from-purple-900/50 to-indigo-900/50 backdrop-blur-sm p-6 md:p-8 rounded-xl border border-purple-500/20 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/25 hover:border-purple-500/40 h-full">
      
      {/* Animated gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      {/* Content */}
      <div className="relative flex items-start gap-4 h-full">
        <IconWrapper
          variant={iconVariant}
          size={24}
          containerStyle={true}
          className="rounded-lg bg-purple-900/50 border border-purple-500/30 group-hover:scale-110 transition-transform duration-300"
        >
          <Icon />
        </IconWrapper>
        <div className="flex-1">
          <h3 className="text-base md:text-lg font-semibold text-purple-300 mb-2">
            {title}
          </h3>
          <p className="text-sm md:text-base text-gray-300 group-hover:text-white transition-colors duration-300 mb-2">
            {value}
          </p>
          {description && (
            <p className="text-gray-400 text-sm">{description}</p>
          )}
        </div>
        <IconWrapper
          variant="default"
          size={20}
          className="opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all duration-300"
        >
          <ArrowUpRight />
        </IconWrapper>
      </div>
    </div>
  </motion.a>
);

// Contact Form Component
const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    budget: '',
    message: '',
    timeline: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const projectTypes = [
    'Web Development', 'Mobile App', 'AI Solution', 'Consultation', 'MVP Development', 'Other'
  ];

  const budgetRanges = [
    'Under $5K', '$5K - $15K', '$15K - $50K', '$50K - $100K', '$100K+', 'Let\'s discuss'
  ];

  const timelines = [
    'ASAP', '1-2 months', '3-6 months', '6+ months', 'Flexible'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear errors when user starts typing
    if (errors[name] || errors.submit) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        delete newErrors.submit;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
        // Reset form after 5 seconds
        setTimeout(() => {
          setSubmitted(false);
          setFormData({
            name: '', email: '', company: '', projectType: '', budget: '', message: '', timeline: ''
          });
        }, 5000);
      } else {
        // Handle API errors
        setErrors({ submit: data.error || 'Failed to send message. Please try again.' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: 'Network error. Please check your connection and try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative bg-gradient-to-br from-purple-900/30 to-indigo-900/30 backdrop-blur-sm rounded-2xl border border-purple-500/20 p-6 md:p-8"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-indigo-500/5 rounded-2xl" />
      
      <div className="relative">
        <h3 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
          <IconWrapper variant="neon" size={28}>
            <Send />
          </IconWrapper>
          Tell Us About Your Project
        </h3>

        <AnimatePresence mode="wait">
          {submitted ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="text-center py-12"
            >
              <IconWrapper variant="neon" size={64} className="mx-auto mb-4">
                <CheckCircle />
              </IconWrapper>
              <h4 className="text-2xl font-bold text-white mb-2">Message Sent Successfully!</h4>
              <p className="text-gray-300">We'll get back to you within 24 hours.</p>
              <p className="text-purple-400 text-sm mt-4">
                Check your email for a confirmation message.
              </p>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white font-medium mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg bg-black/30 border ${
                      errors.name ? 'border-red-400' : 'border-purple-500/30 focus:border-purple-400'
                    } text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 transition-all duration-300`}
                    placeholder="Your name"
                  />
                  {errors.name && (
                    <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle size={14} /> {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg bg-black/30 border ${
                      errors.email ? 'border-red-400' : 'border-purple-500/30 focus:border-purple-400'
                    } text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 transition-all duration-300`}
                    placeholder="your.email@company.com"
                  />
                  {errors.email && (
                    <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle size={14} /> {errors.email}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white font-medium mb-2">Company</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/30 border border-purple-500/30 focus:border-purple-400 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 transition-all duration-300"
                    placeholder="Company name (optional)"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Project Type</label>
                  <select
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/30 border border-purple-500/30 focus:border-purple-400 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/30 transition-all duration-300"
                  >
                    <option value="" className="bg-black">Select project type</option>
                    {projectTypes.map(type => (
                      <option key={type} value={type} className="bg-black">{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white font-medium mb-2">Budget Range</label>
                  <select
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/30 border border-purple-500/30 focus:border-purple-400 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/30 transition-all duration-300"
                  >
                    <option value="" className="bg-black">Select budget range</option>
                    {budgetRanges.map(budget => (
                      <option key={budget} value={budget} className="bg-black">{budget}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Timeline</label>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/30 border border-purple-500/30 focus:border-purple-400 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/30 transition-all duration-300"
                  >
                    <option value="" className="bg-black">Select timeline</option>
                    {timelines.map(timeline => (
                      <option key={timeline} value={timeline} className="bg-black">{timeline}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Project Details *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  className={`w-full px-4 py-3 rounded-lg bg-black/30 border ${
                    errors.message ? 'border-red-400' : 'border-purple-500/30 focus:border-purple-400'
                  } text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 transition-all duration-300 resize-none`}
                  placeholder="Tell us about your project, goals, and any specific requirements..."
                />
                {errors.message && (
                  <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={14} /> {errors.message}
                  </p>
                )}
              </div>

              {/* General form error */}
              {errors.submit && (
                <div className="bg-red-900/50 border border-red-500/50 rounded-lg p-4 flex items-center gap-3">
                  <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
                  <p className="text-red-300">{errors.submit}</p>
                </div>
              )}

              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                className="w-full px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-purple-500 hover:to-indigo-500 transition-all duration-300 shadow-lg shadow-purple-600/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </motion.button>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// FAQ Component
const FAQItem = ({ question, answer, delay = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className="border border-purple-500/20 rounded-lg overflow-hidden"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 text-left bg-gradient-to-br from-purple-900/30 to-indigo-900/30 hover:from-purple-900/50 hover:to-indigo-900/50 transition-all duration-300 flex items-center justify-between"
      >
        <span className="font-medium text-white">{question}</span>
        <motion.div
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-purple-400"
        >
          <ArrowUpRight />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 py-4 bg-black/20 border-t border-purple-500/10">
              <p className="text-gray-300">{answer}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Main Contact Component
const Contact = () => {
  const faqs = [
    {
      question: "How long does a typical project take?",
      answer: "Project timelines vary based on complexity. Simple websites take 2-4 weeks, while complex web applications can take 2-6 months. We'll provide a detailed timeline during our initial consultation."
    },
    {
      question: "Do you offer ongoing support and maintenance?",
      answer: "Yes! We provide comprehensive support packages including bug fixes, updates, security monitoring, and feature enhancements to keep your project running smoothly."
    },
    {
      question: "What's included in your free consultation?",
      answer: "Our free consultation includes project scope discussion, technology recommendations, timeline estimation, budget planning, and a roadmap for your project's success."
    },
    {
      question: "Can you work with our existing development team?",
      answer: "Absolutely! We're experienced in collaborating with existing teams, providing expertise where needed, or taking full ownership of specific project components."
    },
    {
      question: "Do you provide AI and machine learning solutions?",
      answer: "Yes, we specialize in AI-powered applications including chatbots, voice assistants, predictive analytics, and custom machine learning implementations."
    }
  ];

  return (
    <div className="relative min-h-screen py-16 md:py-24">
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Animated grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:48px_48px] animate-pulse" />
        
        {/* Multiple glowing orbs */}
        <div className="absolute top-1/6 left-1/6 w-64 md:w-96 h-64 md:h-96 bg-purple-500/20 rounded-full blur-[128px] animate-pulse" />
        <div className="absolute top-1/2 right-1/6 w-48 md:w-72 h-48 md:h-72 bg-indigo-500/25 rounded-full blur-[96px] animate-pulse delay-500" />
        <div className="absolute bottom-1/4 left-1/3 w-56 md:w-80 h-56 md:h-80 bg-pink-500/15 rounded-full blur-[112px] animate-pulse delay-1000" />
      </div>

      <div className="container mx-auto px-4 relative z-10 space-y-16 md:space-y-24">
        {/* Hero Header */}
        <div className="text-center max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400"
          >
            Let's Build Something Amazing Together
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg md:text-xl text-gray-300"
          >
            Ready to transform your ideas into reality? Reach out to us through any of these channels.
          </motion.p>
        </div>

        {/* Quick Contact Methods */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
          <ContactCard
            icon={Calendar}
            title="Free Consultation"
            value="Book a call"
            description="30-minute strategy session"
            link="https://cal.com/tugadot"
            delay={0.1}
            iconVariant="neon"
          />
          <ContactCard
            icon={Mail}
            title="Email Us"
            value="info@tugadot.com"
            description="Quick response guaranteed"
            link="mailto:info@tugadot.com"
            delay={0.2}
            iconVariant="neon"
          />
          <ContactCard
            icon={Phone}
            title="Call Direct"
            value="+61 484 078 856"
            description="Available business hours"
            link="tel:+61484078856"
            delay={0.3}
            iconVariant="neon"
          />
          <ContactCard
            icon={MessageSquare}
            title="WhatsApp"
            value="Instant messaging"
            description="Real-time communication"
            link="https://wa.me/61484078856"
            delay={0.4}
            iconVariant="neon"
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
          {/* Contact Form - Takes 2 columns */}
          <div className="lg:col-span-2">
            <ContactForm />
          </div>

          {/* Sidebar Information */}
          <div className="space-y-8">
            {/* Office Information */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 backdrop-blur-sm rounded-xl border border-purple-500/20 p-6"
            >
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <IconWrapper variant="purple" size={24}>
                  <Building />
                </IconWrapper>
                Office Hours
              </h3>
              <div className="space-y-3 text-gray-300">
                <div className="flex items-center gap-3">
                  <Clock size={16} className="text-purple-400" />
                  <div>
                    <p className="font-medium">Monday - Friday</p>
                    <p className="text-sm">9:00 AM - 6:00 PM AEST</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin size={16} className="text-purple-400" />
                  <div>
                    <p className="font-medium">Based in Australia</p>
                    <p className="text-sm">Serving clients worldwide</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* What to Expect */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 backdrop-blur-sm rounded-xl border border-purple-500/20 p-6"
            >
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <IconWrapper variant="neon" size={24}>
                  <Zap />
                </IconWrapper>
                What to Expect
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle size={16} className="text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-white">Quick Response</p>
                    <p className="text-sm text-gray-300">We respond within 24 hours</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle size={16} className="text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-white">Detailed Proposal</p>
                    <p className="text-sm text-gray-300">Comprehensive project roadmap</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle size={16} className="text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-white">Free Consultation</p>
                    <p className="text-sm text-gray-300">30-minute strategy session</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-300 text-lg">
              Got questions? We've got answers. Find everything you need to know about working with us.
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                delay={index * 0.1}
              />
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center bg-gradient-to-r from-purple-600/20 to-indigo-600/20 backdrop-blur-sm rounded-2xl border border-purple-500/30 p-8 md:p-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Join dozens of satisfied clients who've transformed their business with our solutions. 
            Let's discuss how we can help you achieve your goals.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <motion.button
              onClick={() => window.open("https://cal.com/tugadot/quickconnect", "_blank")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-purple-500 hover:to-indigo-500 transition-all duration-300 shadow-lg shadow-purple-600/20 flex items-center gap-2"
            >
              <Calendar size={18} />
              Schedule Free Consultation
            </motion.button>
            <motion.a
              href="mailto:info@tugadot.com"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 border-2 border-purple-500/50 text-white font-semibold rounded-lg hover:bg-purple-500/10 transition-all duration-300 flex items-center gap-2"
            >
              <Mail size={18} />
              Send Email Instead
            </motion.a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;